import { useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  ActionBar,
  Alert,
  Button,
  EmptyState,
  Filters,
  Input,
  LoadingState,
  Pagination,
  SectionHeader,
  Select,
  StatusChip,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
} from "@/components/ui";
import { apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { useHubEmbedded } from "@/modules/hub-kit/HubKit";
import { createMarketingApi } from "../api";
import {
  audienceStatusTone,
  deliveryStatusTone,
  formatLabel,
  notificationStatusTone,
  templateStatusTone,
} from "../labels";
import { useMarketingQueue } from "../useMarketingQueue";
import type {
  AudienceDto,
  DeliveryDto,
  NotificationDto,
  TemplateDto,
} from "../types";
import {
  CreateAudienceDialog,
  CreateCampaignDialog,
  CreateTemplateDialog,
} from "../components/MarketingDialogs";

const NOTIFICATION_STATUSES = [
  "draft",
  "pending_approval",
  "scheduled",
  "sent",
  "delivered",
  "failed",
  "cancelled",
  "archived",
] as const;

const TEMPLATE_STATUSES = [
  "draft",
  "pending_approval",
  "approved",
  "retired",
] as const;

const AUDIENCE_STATUSES = [
  "draft",
  "approved",
  "active",
  "updated",
  "retired",
] as const;

const DELIVERY_STATUSES = [
  "pending",
  "sent",
  "delivered",
  "failed",
  "retrying",
  "cancelled",
  "suppressed",
] as const;

const CHANNELS = ["in_app", "push", "email", "future_governed_channel"] as const;

const TYPES = [
  "marketing",
  "operational",
  "system",
  "policy",
  "payment",
  "booking",
  "account_status",
  "enforcement",
  "incident",
] as const;

function QueueShell({
  title,
  description,
  breadcrumbs,
  filters,
  actions,
  error,
  loading,
  empty,
  children,
  page,
  nextCursor,
  onNext,
  onPrevious,
  onRefresh,
}: {
  title: string;
  description: string;
  breadcrumbs: { label: string; to?: string }[];
  filters: ReactNode;
  actions?: ReactNode;
  error: string | null;
  loading: boolean;
  empty: boolean;
  children: ReactNode;
  page: number;
  nextCursor: string | null;
  onNext: () => void;
  onPrevious: () => void;
  onRefresh: () => void;
}) {
  const embedded = useHubEmbedded();
  usePageBreadcrumbs(embedded ? embedded.crumbs : breadcrumbs);

  return (
    <Stack gap={24}>
      <SectionHeader
        title={title}
        description={description}
        actions={
          <Flex gap={8} wrap>
            {actions}
            <Button variant="secondary" onClick={onRefresh}>
              Refresh
            </Button>
          </Flex>
        }
      />
      <Filters>{filters}</Filters>
      {error ? (
        <Alert tone="danger" title="Load failed">
          {error}
        </Alert>
      ) : null}
      {loading ? (
        <LoadingState message={`Loading ${title}…`} />
      ) : null}
      {!loading && !error && empty ? (
        <EmptyState
          title={`No ${title.toLowerCase()}`}
          description="Adjust filters or create a draft record."
          reason="none"
        />
      ) : null}
      {!loading && !empty ? (
        <Stack gap={12}>
          <ActionBar
            left={
              <Text role="small" color="secondary" as="span">
                Page {page}
              </Text>
            }
            right={
              <Pagination
                page={page}
                pageCount={nextCursor ? page + 1 : page}
                onPageChange={(nextPage) => {
                  if (nextPage > page) onNext();
                  if (nextPage < page) onPrevious();
                }}
              />
            }
          />
          {children}
        </Stack>
      ) : null}
    </Stack>
  );
}

export function CampaignsQueuePage() {
  const navigate = useNavigate();
  const client = useApiClient();
  const api = useMemo(() => createMarketingApi(client), [client]);
  const toast = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const loadPage = useMemo(
    () =>
      async (params: {
        cursor?: string;
        status: string;
        sort: string;
        extra: Record<string, string>;
      }) => {
        const list = await api.listNotifications({
          ...(params.cursor ? { cursor: params.cursor } : {}),
          limit: 25,
          ...(params.status ? { status: params.status } : {}),
          ...(params.sort ? { sort: params.sort } : {}),
          ...(params.extra.notification_type
            ? { notification_type: params.extra.notification_type }
            : {}),
          ...(params.extra.channel ? { channel: params.extra.channel } : {}),
          ...(params.extra.audience_id
            ? { audience_id: params.extra.audience_id }
            : {}),
          ...(params.extra.recipient_user_id
            ? { recipient_user_id: params.extra.recipient_user_id }
            : {}),
          ...(params.extra.notification_template_id
            ? {
                notification_template_id:
                  params.extra.notification_template_id,
              }
            : {}),
        });
        return {
          items: list.notifications,
          pagination: list.pagination,
        };
      },
    [api],
  );

  const queue = useMarketingQueue<NotificationDto>(loadPage, {
    sort: "-id",
    extra: { notification_type: "marketing" },
  });

  return (
    <>
      <QueueShell
        title="Campaigns"
        description="Marketing notifications (default filter notification_type=marketing). Draft → submit_approval → approve/schedule. Sending and prepare-deliveries are not offered here."
        breadcrumbs={[
          { label: "HUDDL HQ", to: "/app" },
          { label: "Marketing", to: "/app/marketing" },
          { label: "Campaigns" },
        ]}
        actions={
          <Button onClick={() => setCreateOpen(true)}>Create draft</Button>
        }
        filters={
          <Flex gap={12} wrap>
            <Select
              label="Status"
              value={queue.status}
              onChange={(event) => queue.setStatus(event.target.value)}
              options={[
                { value: "", label: "All statuses" },
                ...NOTIFICATION_STATUSES.map((value) => ({
                  value,
                  label: formatLabel(value),
                })),
              ]}
            />
            <Select
              label="Type"
              value={queue.extra.notification_type ?? "marketing"}
              onChange={(event) =>
                queue.setExtra("notification_type", event.target.value)
              }
              options={[
                { value: "", label: "All types" },
                ...TYPES.map((value) => ({
                  value,
                  label: formatLabel(value),
                })),
              ]}
            />
            <Select
              label="Channel"
              value={queue.extra.channel ?? ""}
              onChange={(event) => queue.setExtra("channel", event.target.value)}
              options={[
                { value: "", label: "All channels" },
                ...CHANNELS.map((value) => ({
                  value,
                  label: formatLabel(value),
                })),
              ]}
            />
            <Input
              label="Audience ID"
              value={queue.extra.audience_id ?? ""}
              onChange={(event) =>
                queue.setExtra("audience_id", event.target.value)
              }
            />
            <Select
              label="Sort"
              value={queue.sort}
              onChange={(event) => queue.setSort(event.target.value)}
              options={[
                { value: "-id", label: "Newest ID" },
                { value: "id", label: "Oldest ID" },
                { value: "status", label: "Status" },
                { value: "scheduled_at", label: "Scheduled" },
              ]}
            />
            <Button variant="secondary" onClick={() => queue.clearFilters()}>
              Reset
            </Button>
          </Flex>
        }
        error={queue.error}
        loading={queue.loading}
        empty={queue.rows.length === 0}
        page={queue.page}
        nextCursor={queue.nextCursor}
        onNext={() => queue.goNext()}
        onPrevious={() => queue.goPrevious()}
        onRefresh={() => void queue.load()}
      >
        <Table>
          <Thead>
            <Tr>
              <Th>ID</Th>
              <Th>Type</Th>
              <Th>Channel</Th>
              <Th>Status</Th>
              <Th>Audience</Th>
              <Th>Scheduled</Th>
            </Tr>
          </Thead>
          <Tbody>
            {queue.rows.map((row) => (
              <Tr
                key={row.id}
                onClick={() =>
                  void navigate(`/app/marketing/campaigns/${row.id}`)
                }
              >
                <Td>{row.id}</Td>
                <Td>{formatLabel(row.notification_type)}</Td>
                <Td>{formatLabel(row.channel)}</Td>
                <Td>
                  <StatusChip tone={notificationStatusTone(row.status)}>
                    {formatLabel(row.status)}
                  </StatusChip>
                </Td>
                <Td>{row.audience_id ?? row.recipient_user_id ?? "—"}</Td>
                <Td>{row.scheduled_at ?? "—"}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </QueueShell>

      <CreateCampaignDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        busy={createBusy}
        error={createError}
        onSubmit={async (body) => {
          setCreateBusy(true);
          setCreateError(null);
          try {
            const result = await api.createNotification(body);
            toast.push({ title: "Draft campaign created", tone: "success" });
            setCreateOpen(false);
            void navigate(`/app/marketing/campaigns/${result.notification.id}`);
          } catch (err) {
            setCreateError(apiErrorMessage(err));
          } finally {
            setCreateBusy(false);
          }
        }}
      />
    </>
  );
}

export function TemplatesQueuePage() {
  const navigate = useNavigate();
  const client = useApiClient();
  const api = useMemo(() => createMarketingApi(client), [client]);
  const toast = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const loadPage = useMemo(
    () =>
      async (params: {
        cursor?: string;
        status: string;
        sort: string;
        extra: Record<string, string>;
      }) => {
        const list = await api.listTemplates({
          ...(params.cursor ? { cursor: params.cursor } : {}),
          limit: 25,
          ...(params.status ? { status: params.status } : {}),
          ...(params.sort ? { sort: params.sort } : {}),
          ...(params.extra.notification_type
            ? { notification_type: params.extra.notification_type }
            : {}),
          ...(params.extra.channel ? { channel: params.extra.channel } : {}),
          ...(params.extra.owner_user_id
            ? { owner_user_id: params.extra.owner_user_id }
            : {}),
        });
        return { items: list.templates, pagination: list.pagination };
      },
    [api],
  );

  const queue = useMarketingQueue<TemplateDto>(loadPage, { sort: "-id" });

  return (
    <>
      <QueueShell
        title="Templates"
        description="Notification templates for Marketing and other governed types. Approve before use. Does not transmit content."
        breadcrumbs={[
          { label: "HUDDL HQ", to: "/app" },
          { label: "Marketing", to: "/app/marketing" },
          { label: "Templates" },
        ]}
        actions={
          <Button onClick={() => setCreateOpen(true)}>Create draft</Button>
        }
        filters={
          <Flex gap={12} wrap>
            <Select
              label="Status"
              value={queue.status}
              onChange={(event) => queue.setStatus(event.target.value)}
              options={[
                { value: "", label: "All statuses" },
                ...TEMPLATE_STATUSES.map((value) => ({
                  value,
                  label: formatLabel(value),
                })),
              ]}
            />
            <Select
              label="Type"
              value={queue.extra.notification_type ?? ""}
              onChange={(event) =>
                queue.setExtra("notification_type", event.target.value)
              }
              options={[
                { value: "", label: "All types" },
                ...TYPES.map((value) => ({
                  value,
                  label: formatLabel(value),
                })),
              ]}
            />
            <Select
              label="Channel"
              value={queue.extra.channel ?? ""}
              onChange={(event) => queue.setExtra("channel", event.target.value)}
              options={[
                { value: "", label: "All channels" },
                ...CHANNELS.map((value) => ({
                  value,
                  label: formatLabel(value),
                })),
              ]}
            />
            <Input
              label="Owner User ID"
              value={queue.extra.owner_user_id ?? ""}
              onChange={(event) =>
                queue.setExtra("owner_user_id", event.target.value)
              }
            />
            <Button variant="secondary" onClick={() => queue.clearFilters()}>
              Reset
            </Button>
          </Flex>
        }
        error={queue.error}
        loading={queue.loading}
        empty={queue.rows.length === 0}
        page={queue.page}
        nextCursor={queue.nextCursor}
        onNext={() => queue.goNext()}
        onPrevious={() => queue.goPrevious()}
        onRefresh={() => void queue.load()}
      >
        <Table>
          <Thead>
            <Tr>
              <Th>Name</Th>
              <Th>Type</Th>
              <Th>Channel</Th>
              <Th>Status</Th>
              <Th>Classification</Th>
            </Tr>
          </Thead>
          <Tbody>
            {queue.rows.map((row) => (
              <Tr
                key={row.id}
                onClick={() =>
                  void navigate(`/app/marketing/templates/${row.id}`)
                }
              >
                <Td>{row.name}</Td>
                <Td>{formatLabel(row.notification_type)}</Td>
                <Td>{formatLabel(row.channel)}</Td>
                <Td>
                  <StatusChip tone={templateStatusTone(row.status)}>
                    {formatLabel(row.status)}
                  </StatusChip>
                </Td>
                <Td>{formatLabel(row.classification)}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </QueueShell>

      <CreateTemplateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        busy={createBusy}
        error={createError}
        onSubmit={async (body) => {
          setCreateBusy(true);
          setCreateError(null);
          try {
            const result = await api.createTemplate(body);
            toast.push({ title: "Template created", tone: "success" });
            setCreateOpen(false);
            void navigate(`/app/marketing/templates/${result.template.id}`);
          } catch (err) {
            setCreateError(apiErrorMessage(err));
          } finally {
            setCreateBusy(false);
          }
        }}
      />
    </>
  );
}

export function AudiencesQueuePage() {
  const navigate = useNavigate();
  const client = useApiClient();
  const api = useMemo(() => createMarketingApi(client), [client]);
  const toast = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const loadPage = useMemo(
    () =>
      async (params: {
        cursor?: string;
        status: string;
        sort: string;
        extra: Record<string, string>;
      }) => {
        const list = await api.listAudiences({
          ...(params.cursor ? { cursor: params.cursor } : {}),
          limit: 25,
          ...(params.status ? { status: params.status } : {}),
          ...(params.sort ? { sort: params.sort } : {}),
          ...(params.extra.owner_user_id
            ? { owner_user_id: params.extra.owner_user_id }
            : {}),
        });
        return { items: list.audiences, pagination: list.pagination };
      },
    [api],
  );

  const queue = useMarketingQueue<AudienceDto>(loadPage, { sort: "-id" });

  return (
    <>
      <QueueShell
        title="Audiences"
        description="Audience definitions for campaign targeting. Activate is status governance of the definition — not a send. Membership is single-user only."
        breadcrumbs={[
          { label: "HUDDL HQ", to: "/app" },
          { label: "Marketing", to: "/app/marketing" },
          { label: "Audiences" },
        ]}
        actions={
          <Button onClick={() => setCreateOpen(true)}>Create draft</Button>
        }
        filters={
          <Flex gap={12} wrap>
            <Select
              label="Status"
              value={queue.status}
              onChange={(event) => queue.setStatus(event.target.value)}
              options={[
                { value: "", label: "All statuses" },
                ...AUDIENCE_STATUSES.map((value) => ({
                  value,
                  label: formatLabel(value),
                })),
              ]}
            />
            <Input
              label="Owner User ID"
              value={queue.extra.owner_user_id ?? ""}
              onChange={(event) =>
                queue.setExtra("owner_user_id", event.target.value)
              }
            />
            <Button variant="secondary" onClick={() => queue.clearFilters()}>
              Reset
            </Button>
          </Flex>
        }
        error={queue.error}
        loading={queue.loading}
        empty={queue.rows.length === 0}
        page={queue.page}
        nextCursor={queue.nextCursor}
        onNext={() => queue.goNext()}
        onPrevious={() => queue.goPrevious()}
        onRefresh={() => void queue.load()}
      >
        <Table>
          <Thead>
            <Tr>
              <Th>Name</Th>
              <Th>Status</Th>
              <Th>Classification</Th>
              <Th>Purpose</Th>
              <Th>Owner</Th>
            </Tr>
          </Thead>
          <Tbody>
            {queue.rows.map((row) => (
              <Tr
                key={row.id}
                onClick={() =>
                  void navigate(`/app/marketing/audiences/${row.id}`)
                }
              >
                <Td>{row.name}</Td>
                <Td>
                  <StatusChip tone={audienceStatusTone(row.status)}>
                    {formatLabel(row.status)}
                  </StatusChip>
                </Td>
                <Td>{formatLabel(row.classification)}</Td>
                <Td>{row.purpose ?? "—"}</Td>
                <Td>{row.owner_user_id ?? "—"}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </QueueShell>

      <CreateAudienceDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        busy={createBusy}
        error={createError}
        onSubmit={async (body) => {
          setCreateBusy(true);
          setCreateError(null);
          try {
            const result = await api.createAudience(body);
            toast.push({ title: "Audience created", tone: "success" });
            setCreateOpen(false);
            void navigate(`/app/marketing/audiences/${result.audience.id}`);
          } catch (err) {
            setCreateError(apiErrorMessage(err));
          } finally {
            setCreateBusy(false);
          }
        }}
      />
    </>
  );
}

export function DeliveriesQueuePage() {
  const navigate = useNavigate();
  const client = useApiClient();
  const api = useMemo(() => createMarketingApi(client), [client]);

  const loadPage = useMemo(
    () =>
      async (params: {
        cursor?: string;
        status: string;
        sort: string;
        extra: Record<string, string>;
      }) => {
        const list = await api.listDeliveries({
          ...(params.cursor ? { cursor: params.cursor } : {}),
          limit: 25,
          ...(params.status ? { status: params.status } : {}),
          ...(params.sort ? { sort: params.sort } : {}),
          ...(params.extra.notification_id
            ? { notification_id: params.extra.notification_id }
            : {}),
          ...(params.extra.recipient_user_id
            ? { recipient_user_id: params.extra.recipient_user_id }
            : {}),
          ...(params.extra.channel ? { channel: params.extra.channel } : {}),
        });
        return { items: list.deliveries, pagination: list.pagination };
      },
    [api],
  );

  const queue = useMarketingQueue<DeliveryDto>(loadPage, { sort: "-id" });

  return (
    <QueueShell
      title="Deliveries"
      description="Per-recipient delivery status from the API (performance / audit timestamps). HQ remediation does not push email or push notifications."
      breadcrumbs={[
        { label: "HUDDL HQ", to: "/app" },
        { label: "Marketing", to: "/app/marketing" },
        { label: "Deliveries" },
      ]}
      filters={
        <Flex gap={12} wrap>
          <Select
            label="Status"
            value={queue.status}
            onChange={(event) => queue.setStatus(event.target.value)}
            options={[
              { value: "", label: "All statuses" },
              ...DELIVERY_STATUSES.map((value) => ({
                value,
                label: formatLabel(value),
              })),
            ]}
          />
          <Input
            label="Notification ID"
            value={queue.extra.notification_id ?? ""}
            onChange={(event) =>
              queue.setExtra("notification_id", event.target.value)
            }
          />
          <Input
            label="Recipient User ID"
            value={queue.extra.recipient_user_id ?? ""}
            onChange={(event) =>
              queue.setExtra("recipient_user_id", event.target.value)
            }
          />
          <Select
            label="Channel"
            value={queue.extra.channel ?? ""}
            onChange={(event) => queue.setExtra("channel", event.target.value)}
            options={[
              { value: "", label: "All channels" },
              ...CHANNELS.map((value) => ({
                value,
                label: formatLabel(value),
              })),
            ]}
          />
          <Button variant="secondary" onClick={() => queue.clearFilters()}>
            Reset
          </Button>
        </Flex>
      }
      error={queue.error}
      loading={queue.loading}
      empty={queue.rows.length === 0}
      page={queue.page}
      nextCursor={queue.nextCursor}
      onNext={() => queue.goNext()}
      onPrevious={() => queue.goPrevious()}
      onRefresh={() => void queue.load()}
    >
      <Table>
        <Thead>
          <Tr>
            <Th>ID</Th>
            <Th>Notification</Th>
            <Th>Recipient</Th>
            <Th>Channel</Th>
            <Th>Status</Th>
            <Th>Retries</Th>
          </Tr>
        </Thead>
        <Tbody>
          {queue.rows.map((row) => (
            <Tr
              key={row.id}
              onClick={() =>
                void navigate(`/app/marketing/deliveries/${row.id}`)
              }
            >
              <Td>{row.id}</Td>
              <Td>{row.notification_id}</Td>
              <Td>{row.recipient_user_id}</Td>
              <Td>{formatLabel(row.channel)}</Td>
              <Td>
                <StatusChip tone={deliveryStatusTone(row.status)}>
                  {formatLabel(row.status)}
                </StatusChip>
              </Td>
              <Td>{row.retry_count}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </QueueShell>
  );
}
